// src/pages/purchases/EditPurchase.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import API_BASE_URL from "../../utils/config";

// ---- small date helpers (store ISO, show yyyy-mm-dd; hint dd-mm-yyyy) ----
const toInputDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const fromInputDate = (ymd) => {
  if (!ymd) return "";
  // keep as ISO (UTC midnight)
  return new Date(ymd + "T00:00:00.000Z").toISOString();
};

export default function EditPurchase() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    supplier_name: "",
    invoice_number: "",
    invoice_date: "",
    total_amount: "",
    items: [],
  });

  const fetchPurchase = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/purchases/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { order, items } = res.data || {};
      setForm({
        supplier_name: order?.supplier_name ?? "",
        invoice_number: order?.invoice_number ?? "",
        invoice_date: order?.invoice_date ?? "",
        total_amount: order?.total_amount ?? "",
        items: (items || []).map((it) => ({
          id: it.id,
          product_id: it.product_id,
          product_name: it.product_name,
          batch_number: it.batch_number ?? it.batch_no ?? "",
          expiry_date: it.expiry_date ?? "",
          quantity: Number(it.quantity ?? 0),
          mrp: Number(it.mrp ?? it.rate ?? 0),
          purchase_rate: Number(it.purchase_rate ?? it.rate ?? 0),
        })),
      });
    } catch (e) {
      console.error(e);
      setError("Failed to load purchase.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleItemChange = (idx, field, value) => {
    setForm((f) => {
      const items = [...f.items];
      const row = { ...items[idx] };
      if (["quantity", "mrp", "purchase_rate"].includes(field)) {
        const num = value === "" ? "" : Number(value);
        row[field] = Number.isFinite(num) ? num : 0;
      } else {
        row[field] = value;
      }
      items[idx] = row;
      return { ...f, items };
    });
  };

  // auto compute total from items (purchase_rate * qty). If blank, fall back to form.total_amount
  const computedTotal = useMemo(() => {
    if (!Array.isArray(form.items)) return 0;
    return form.items.reduce((sum, it) => {
      const q = Number(it.quantity || 0);
      const r = Number(
        (it.purchase_rate !== "" && it.purchase_rate != null)
          ? it.purchase_rate
          : (it.mrp || 0)
      );
      return sum + q * r;
    }, 0);
  }, [form.items]);

  const displayTotal = useMemo(() => {
    const manual = Number(form.total_amount || 0);
    // if manual is empty string, show computed; else show manual but hint computed
    return isNaN(manual) || manual === 0 ? computedTotal : manual;
  }, [computedTotal, form.total_amount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError("");
      const token = localStorage.getItem("token");
      // send ISO date
      const payload = {
        ...form,
        invoice_date: form.invoice_date ? form.invoice_date : null,
        items: form.items.map((it) => ({
          id: it.id,
          product_id: it.product_id,
          batch_number: it.batch_number,
          expiry_date: it.expiry_date || null,
          quantity: Number(it.quantity || 0),
          mrp: Number(it.mrp || 0),
          purchase_rate: Number(it.purchase_rate || 0),
        })),
      };
      await axios.put(`${API_BASE_URL}/api/purchases/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate(`/purchases/${id}`);
    } catch (e) {
      console.error(e);
      setError("Failed to update purchase.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-8 w-48 bg-gray-200 rounded mb-4" />
        <div className="animate-pulse h-72 w-full bg-gray-100 rounded" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 w-full">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-semibold tracking-tight">Edit Purchase</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm px-3 py-2 rounded border border-gray-300 hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Header Card */}
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-6">
          <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Supplier
              </label>
              <input
                type="text"
                value={form.supplier_name}
                onChange={(e) => handleChange({ supplier_name: e.target.value })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                placeholder="Supplier Name"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Invoice #
              </label>
              <input
                type="text"
                value={form.invoice_number}
                onChange={(e) => handleChange({ invoice_number: e.target.value })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                placeholder="e.g. B009673"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Invoice Date <span className="text-gray-400">(dd-mm-yyyy)</span>
              </label>
              <input
                type="date"
                value={toInputDate(form.invoice_date)}
                onChange={(e) =>
                  handleChange({ invoice_date: fromInputDate(e.target.value) })
                }
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>

            <div className="md:col-span-1">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Total Amount (₹)
              </label>
              <input
                type="number"
                step="0.01"
                value={form.total_amount}
                onChange={(e) => handleChange({ total_amount: e.target.value })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-right focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                placeholder={computedTotal.toFixed(2)}
              />
              <p className="mt-1 text-[11px] text-gray-500">
                Auto-computed: <span className="font-medium">₹{computedTotal.toFixed(2)}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-xs uppercase tracking-wide text-gray-600">
                  <th className="text-left px-4 py-3">Product</th>
                  <th className="text-left px-2 py-3 w-44">Batch No.</th>
                  <th className="text-left px-2 py-3 w-48">Expiry <span className="text-gray-400 lowercase">(dd-mm-yyyy)</span></th>
                  <th className="text-right px-2 py-3 w-32">Qty</th>
                  <th className="text-right px-2 py-3 w-36">MRP (₹)</th>
                  <th className="text-right px-4 py-3 w-40">Purchase Rate (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {form.items.map((it, idx) => {
                  const isExpiring =
                    it.expiry_date &&
                    new Date(it.expiry_date).getTime() <=
                      new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getTime();
                  return (
                    <tr key={it.id || idx} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5">
                        <div className="text-sm font-medium text-gray-800">{it.product_name}</div>
                        <div className="text-[11px] text-gray-500">ID: {it.product_id}</div>
                      </td>

                      <td className="px-2 py-2.5">
                        <input
                          type="text"
                          value={it.batch_number || ""}
                          onChange={(e) =>
                            handleItemChange(idx, "batch_number", e.target.value)
                          }
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                          placeholder="Batch #"
                        />
                      </td>

                      <td className="px-2 py-2.5">
                        <input
                          type="date"
                          value={toInputDate(it.expiry_date)}
                          onChange={(e) =>
                            handleItemChange(idx, "expiry_date", fromInputDate(e.target.value))
                          }
                          className={`w-full rounded-lg border px-2.5 py-2 focus:outline-none focus:ring-2 ${
                            isExpiring
                              ? "border-red-300 focus:ring-red-400/30"
                              : "border-gray-200 focus:ring-blue-500/30"
                          }`}
                        />
                        {isExpiring && (
                          <div className="text-[11px] mt-1 text-red-600">
                            Expiring / expired this month
                          </div>
                        )}
                      </td>

                      <td className="px-2 py-2.5 text-right">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={it.quantity}
                          onChange={(e) =>
                            handleItemChange(idx, "quantity", e.target.value)
                          }
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-right focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        />
                      </td>

                      <td className="px-2 py-2.5 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={it.mrp}
                          onChange={(e) => handleItemChange(idx, "mrp", e.target.value)}
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-right focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        />
                      </td>

                      <td className="px-4 py-2.5 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={it.purchase_rate}
                          onChange={(e) =>
                            handleItemChange(idx, "purchase_rate", e.target.value)
                          }
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-right focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Table Footer (Totals) */}
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-600" colSpan={4}>
                    Rows: <span className="font-medium">{form.items.length}</span>
                  </td>
                  <td className="px-2 py-3 text-right text-sm text-gray-600">
                    Computed Total:
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">
                    ₹{computedTotal.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Sticky Save Bar */}
        <div className="h-16" />
        <div className="fixed bottom-4 left-0 right-0">
          <div className="mx-auto max-w-[1400px] px-4">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-lg shadow-gray-200/40 p-3 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Display Total:&nbsp;
                <span className="font-semibold">₹{Number(displayTotal || 0).toFixed(2)}</span>
                {form.total_amount && (
                  <span className="ml-2 text-gray-500">
                    (Auto: ₹{computedTotal.toFixed(2)})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-4 py-2 rounded-xl border border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Update Purchase"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
